import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../../css/CommandCenterPickerModal.css';

const SOURCE_LABELS = {
    command_center: 'Command Center library',
    documentation: 'Documentation',
    team_workspaces: 'Team Workspaces',
    task_management: 'Task Management',
    audio_transcription: 'Audio Transcription',
    document_markup: 'Document Markup',
};

/**
 * CommandCenterPickerModal
 *
 * Lists every file the user owns across the Bluebird Command Center
 * (cc.bluebird-documentation.com) - loose library files plus attachments
 * from all apps - and returns the chosen one as a File object, so it flows
 * through the existing attachment upload path (copy-on-attach).
 *
 * Props:
 *   apiCall      - authenticated fetch helper (path, options) => Response
 *   onClose      - dismiss the modal
 *   onSelect     - called with a File object when a file is picked
 *   maxSizeBytes - files above this are shown disabled (default 25MB)
 */
const CommandCenterPickerModal = ({ apiCall, onClose, onSelect, maxSizeBytes = 25 * 1024 * 1024 }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fetchingId, setFetchingId] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiCall('/command-center/files/');
                if (!res.ok) throw new Error(`Failed to load your Command Center files (${res.status})`);
                const body = await res.json();
                if (!cancelled) setFiles(body.files || []);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [apiCall]);

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const visibleFiles = search
        ? files.filter((f) => (f.name || '').toLowerCase().includes(search.toLowerCase()))
        : files;

    const handlePick = async (item) => {
        setFetchingId(`${item.source}:${item.id}`);
        setError('');
        try {
            const res = await apiCall(item.download_path);
            if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`);
            const blob = await res.blob();
            const file = new File([blob], item.name, { type: item.file_type || blob.type });
            onSelect(file);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setFetchingId(null);
        }
    };

    return createPortal(
        <div className="CCPicker__overlay" onClick={onClose}>
            <div className="CCPicker" onClick={(e) => e.stopPropagation()}>
                <div className="CCPicker__header">
                    <div className="CCPicker__header-left">
                        <i className="fas fa-box-open" />
                        <h2>Choose from Command Center</h2>
                    </div>
                    <button className="CCPicker__close" onClick={onClose} title="Close">
                        <i className="fas fa-times" />
                    </button>
                </div>
                <div className="CCPicker__body">
                    {error && <p className="CCPicker__error">{error}</p>}
                    {!loading && files.length > 0 && (
                        <input
                            type="search"
                            className="CCPicker__search"
                            placeholder="Search files…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    )}
                    {loading ? (
                        <p className="CCPicker__status">Loading your library…</p>
                    ) : files.length === 0 && !error ? (
                        <p className="CCPicker__status">
                            No files found in your Command Center. Upload files at{' '}
                            <a href="https://cc.bluebird-documentation.com/files" target="_blank" rel="noreferrer">
                                cc.bluebird-documentation.com
                            </a>.
                        </p>
                    ) : visibleFiles.length === 0 ? (
                        <p className="CCPicker__status">No files match “{search}”.</p>
                    ) : (
                        <ul className="CCPicker__list">
                            {visibleFiles.map((item) => {
                                const rowKey = `${item.source}:${item.id}`;
                                const blocked = !item.download_path
                                    ? 'no download available'
                                    : (maxSizeBytes && item.size_bytes > maxSizeBytes)
                                        ? `larger than the ${Math.round(maxSizeBytes / (1024 * 1024))}MB limit`
                                        : null;
                                return (
                                    <li key={rowKey} className="CCPicker__item">
                                        <div className="CCPicker__item-info">
                                            <span className="CCPicker__item-name">{item.name}</span>
                                            <span className="CCPicker__item-meta">
                                                {SOURCE_LABELS[item.source] || item.source} · {formatFileSize(item.size_bytes)}
                                                {blocked ? ` · ${blocked}` : ''}
                                            </span>
                                        </div>
                                        <button
                                            className="CCPicker__select"
                                            disabled={!!blocked || fetchingId === rowKey}
                                            onClick={() => handlePick(item)}
                                        >
                                            {fetchingId === rowKey
                                                ? <><span className="btn-spinner" /> Fetching…</>
                                                : 'Select'}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CommandCenterPickerModal;
